import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CommonButton from '../components/atom/CommonButton';
import { useTranslation } from 'react-i18next';
import { reSourceBooking } from '@openapi/bookings/bookings';
import { getUsers } from '@openapi/users/users';
import useNotify from '../hooks/useNotify';

// Sentinel group option for agents that belong to no commission group, so they
// stay selectable in the group-first picker (backend allows re-sourcing to them).
const NO_GROUP = '__NO_GROUP__';

const ReSourceBookingModal = ({ booking, onSuccess, showModal, MODAL_TYPE }) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotify();
    const [agents, setAgents] = useState([]);
    const [agentId, setAgentId] = useState('');
    const [commissionGroup, setCommissionGroup] = useState(null);
    const [loading, setLoading] = useState(false);

    const closeAction = () => showModal(MODAL_TYPE.NONE);

    const venueId = booking?.venueId;
    const showId = booking?.timeslot?.showId;
    useEffect(() => {
        if (!venueId) {
            return undefined;
        }
        let active = true;
        (async () => {
            try {
                // showId makes the API return each agent's commission group(s)
                // for this booking's show, shown next to their name below.
                // TODO: replace pageSize:10000 with a typeahead or paged loader
                // once any venue actually approaches that many users.
                const res = await getUsers(venueId, {
                    pageSize: 10000,
                    ...(showId ? { showId } : {}),
                });
                if (active) setAgents(res.users || []);
            } catch (err) {
                notifyError(err);
            }
        })();
        return () => {
            active = false;
        };
    }, [venueId, showId, notifyError]);

    const handleSave = async () => {
        if (!agentId) return;
        try {
            setLoading(true);
            await reSourceBooking(booking.id, { agentId: Number(agentId) });
            notifySuccess(t('bookings.reSource.success'));
            if (onSuccess) onSuccess();
            closeAction();
        } catch (err) {
            notifyError(err?.response?.data?.message || t('errors.unknown'));
        } finally {
            setLoading(false);
        }
    };

    const otherAgents = agents.filter((a) => a.id !== booking?.agentId);

    const commissionGroups = [
        ...new Set(otherAgents.flatMap((a) => a.commissionGroupNames || [])),
    ].sort();

    // Keep agents without a commission group reachable - the backend supports
    // re-sourcing to a group-less agent (commission is cleared), and the previous
    // flat picker listed them.
    const hasGrouplessAgents = otherAgents.some((a) => !a.commissionGroupNames?.length);
    const groupOptions = hasGrouplessAgents ? [...commissionGroups, NO_GROUP] : commissionGroups;

    const groupLabel = (group) =>
        group === NO_GROUP ? t('bookings.reSource.noCommissionGroup') : group;

    const filteredAgents = !commissionGroup
        ? []
        : commissionGroup === NO_GROUP
          ? otherAgents.filter((a) => !a.commissionGroupNames?.length)
          : otherAgents.filter((a) => a.commissionGroupNames?.includes(commissionGroup));

    const selectedAgent = filteredAgents.find((a) => a.id === agentId) || null;

    if (!booking) {
        return null;
    }

    return (
        <Dialog open onClose={closeAction} maxWidth="xs" fullWidth>
            <DialogTitle>{t('bookings.reSource.title')}</DialogTitle>
            <DialogContent>
                <Autocomplete
                    options={groupOptions}
                    value={commissionGroup}
                    getOptionLabel={groupLabel}
                    onChange={(_, val) => {
                        setCommissionGroup(val);
                        setAgentId('');
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label={t('commissionGroups.title')} margin="dense" />
                    )}
                />
                <Autocomplete
                    options={filteredAgents}
                    getOptionLabel={(o) => o.name || ''}
                    value={selectedAgent}
                    disabled={!commissionGroup}
                    onChange={(_, val) => setAgentId(val ? val.id : '')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('bookings.columns.agentName')}
                            margin="dense"
                        />
                    )}
                />
            </DialogContent>
            <DialogActions>
                <CommonButton
                    buttonType="secondary"
                    onClick={closeAction}
                    disabled={loading}
                    label={t('common.cancel')}
                />
                <CommonButton
                    buttonType="primary"
                    onClick={handleSave}
                    disabled={loading || !agentId}
                    label={t('common.save')}
                />
            </DialogActions>
        </Dialog>
    );
};

export default ReSourceBookingModal;
